import { browser } from "$app/environment";
import {
	LayoutSectionType,
	type TagItem,
	type BaseLanding,
	type BrandLogos,
	type Deal,
	type Deals,
	type FilterType,
	type FoodLandingItemsParams,
	type RestaurantDynamic,
	type RestaurantSemiStatic,
	type RestaurantsDynamic,
	type RestaurantsSemiStatic,
	type SortType
} from "$lib/def/landing";
import { fetch$, type Cache } from "$lib/serv/core/cache";
import { hasGeo } from "$lib/util/address";
import { createUpgrader, open, transaction } from "$lib/util/db";
import type { Address } from "proto/address";
import {
	BehaviorSubject,
	EMPTY,
	combineLatest,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	map,
	of,
	pairwise,
	shareReplay,
	switchMap,
	type Observable,
	type ValueFromArray,
} from "rxjs";
import { profile$ } from "./core/auth";
import { getRestaurantState, retrieveRestaurantExposed } from "./restaurant";

/**  For 'global' search */
export const KEY_SEARCH = 'search';
/** switch between restaurants/shops  */
export const KEY_SEARCH_VERTICAL = 'search_vertical';
/** switch between merchant/items  */
export const KEY_SEARCH_FOCUS = 'search_focus';
/** Global item key */
export const KEY_FOOD_ITEM = 'fooditem';
export const KEY_PRIMARY_FOOD_FILTER = 'primary_food_filter';
export const KEY_ITEM_MERCH = 'item_merch';

/**
 * Given how long it takes a customer to order FOOD on average we can devide cache ages into 3 categories:
 * Long: longer than the average order time (stale by the time the user starts a new session)
 * Medium: less than the average order time (stale before the user finishes ordering, just in case)
 * Short: a fraction of the average order time, but long enough to give a good user experience (stale often enough to keep the user updated while browsing)
 */
export const CacheAgeShort = 1000 * 30;
export const CacheAgeMedium = 1000 * 60 * 5;
export const CacheAgeLong = 1000 * 60 * 60 * 1;

/**
 * Cache keys (in order) to extract from URL search params.
 * 
 * When a string array is provided the first string in the array is the KEY, while any
 * string in the array (including the KEY) is search in the query string
 */
export const SPKEYS = [['latitude','lat'],['longitude','lon'],'primary_food_filter','secondary_filters','vertical'] as [string, string][];

/**
 * Cache keys (in order) to extract from post body.
 * 
 * A string is for an exact match. An array: `[KEY, POST_BODY_PROPERTY_MAPPING]`.
 * Non-string property values will be converted to a string using `toString()`.
 */
export const SEARCH_PROPKEYS = [['term',['text']],['latitude',['meta','lat']],['longitude',['meta','lon']],'ids','vertical'] as [string, string | string[]][];

export const DBNAME = 'food-landing';

const subjDbInfo = new BehaviorSubject<Record<string, any> | null>(null);
export const dbInfo$ = subjDbInfo.asObservable();

const upgrader = createUpgrader({
	1: (db) => [
		db.createObjectStore('cache', { keyPath: ['path','latitude','longitude','primary_food_filter','secondary_filters', 'vertical']}),
		db.createObjectStore('restaurants', { keyPath: ['id','type'] }),
	],
	2: (db) => [
		db.createObjectStore('search', { keyPath: ['path','term','latitude','longitude','ids','vertical']}),
	]
});

const pdb = browser ? open(DBNAME, 2, upgrader, undefined, (i) => subjDbInfo.next(i)) : Promise.resolve(null);
export async function getDB() {
	return pdb;
}

/**
 * Clear all object stores from the database.
 * @returns Promise that resolves when all object stores have been cleared.
 */
export async function clearDB() {
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for clearing.');
	}
	if (!db) return;
	const oss = ['cache','restaurants','search'];
	const t = transaction(db, oss, 'readwrite');
	return Promise.all(oss.map(os => t.os(os).clear())).then(() => console.log(`Cleared [${oss.join(', ')}] of IDB ${DBNAME}.`));
}

/**
 * Clear DB on logout and login.
 */
profile$.pipe(
	map(user => !!user),
	distinctUntilChanged(),
	pairwise(), // effectivley skipping the first emit
).subscribe(() => clearDB());

// ############################################################
// LOADING FUNCTIONS
// ############################################################

export type DealsLoader = Cache<Deals> & ReturnType<typeof parseDeals>;

export function createBaseLandingItemsStream($:Observable<Partial<Address> | null>, inclBrands = true, inclDeals = true, useCache = true, refreshDyn?: Observable<void>) {
	if (!browser) return EMPTY; // don't waste time on server-side
	return $.pipe(
		filter(params => !!params && hasGeo(params)),
		switchMap(params => params ? loadBase(params, false) : EMPTY),
		switchMap(cache => {
			let static$: ReturnType<typeof createSemiStaticStream> | Observable<null> = of(null);
			let dynamic$: ReturnType<typeof fetch$<RestaurantsDynamic>> | Observable<null> = of(null);
			let brands$: ReturnType<typeof fetch$<BrandLogos>> | Observable<null> = of(null);
			let deals$: Observable<DealsLoader> | Observable<null> = of(null);
			if (cache.payload) {
				static$ = createSemiStaticStream(cache, useCache);
				dynamic$ = createDynamicStream(cache, refreshDyn);
				
				if (inclBrands) brands$ = createBrandsStream(cache, useCache);
				
				if (inclDeals) deals$ = createDealsStream(cache, useCache);
			}
			return combineLatest([
				of(cache),
				static$,
				dynamic$,
				brands$,
				deals$,
			])
		}),
	);
}

function createSemiStaticStream(layoutLoader: { payload?: { restaurants: { semi_static: string; }; }; }, useCache = true) {
	const urlSS = new URL(`api:/${layoutLoader.payload?.restaurants.semi_static}`);
	// urlSS.searchParams.delete('vertical'); // banners contain cross-vertical entities - UPDATE: we now have Landing Page using the all-vertical LI stream
	const options = { db: getDB(), msMaxCacheAge: useCache ? CacheAgeMedium : 0 };
	return fetch$<RestaurantsSemiStatic>(urlSS, SPKEYS, options, null, registerRestaurantsSemiStatic).pipe(shareReplay(1));
}

function createDynamicStream(layoutLoader: { payload?: { restaurants: { dynamic: string; }; }; }, refreshDyn?: Observable<void>) {
	const urlDyn = new URL(`api:/${layoutLoader.payload?.restaurants.dynamic}`);
	const options = { db: getDB(), msMaxCacheAge: CacheAgeShort };
	return fetch$<RestaurantsDynamic>(urlDyn, SPKEYS, options, null, registerRestaurantsDynamic, refreshDyn?.pipe(map(() => true))).pipe(shareReplay(1));
}

function createBrandsStream(layoutLoader: { payload?: { layout: BaseLanding['layout']; }; }, useCache = true) {
	const urlBrands = layoutLoader.payload?.layout.find(item => item.type === LayoutSectionType.BrandLogos)?.endpoint;
	if (!urlBrands) return of(null);
	const optionsBrands = { db: getDB(), msMaxCacheAge: useCache ? CacheAgeMedium : 0 };
	const url = new URL(`api:/${urlBrands}`);
	return urlBrands ? fetch$<BrandLogos>(url, SPKEYS, optionsBrands) : of(null);
}

function createDealsStream(layoutLoader: { payload?: { deals: string; }; }, useCache = true) {
	const optionsDeals = { db: getDB(), msMaxCacheAge: useCache ? CacheAgeMedium : 0 };
	const url = new URL(`api:/${layoutLoader.payload?.deals}`);
	return fetch$<Deals>(url, SPKEYS, optionsDeals).pipe(
		map(wrapper => ({ ...wrapper, ...parseDeals(wrapper.payload) })),
	);
}


export function loadBase(params: FoodLandingItemsParams & Record<string, unknown>, filtered?:boolean) {
	const url = new URL(`api://landing-items/v6/?lat=${params.latitude}&lon=${params.longitude}`);
	const propsAddr: string[] = [
		'street_number',
		'street_name',
		'suburb',
		'town',
		'postal_code'
	];
	propsAddr.forEach(prop => {
		if (typeof params[prop] === 'undefined') return;
		url.searchParams.set(prop, (params[prop] ?? '').toString());
	})
	if (params.vertical) url.searchParams.set('vertical', params.vertical);
	if (filtered) {
		if (params.primary_food_filter) {
			url.searchParams.set('primary_food_filter', params.primary_food_filter);
		}
		if (params.secondary_filters) {
			url.searchParams.set('secondary_filters', params.secondary_filters);
		}
	}
	return fetch$<BaseLanding>(url, SPKEYS, { db: pdb, msMaxCacheAge: CacheAgeLong });
}

export function obsGeoUpdates($:Observable<FoodLandingItemsParams>) {
	return $.pipe(
		distinctUntilChanged((prev,cur) => true
			&& prev?.latitude === cur?.latitude
			&& prev?.longitude === cur?.longitude
			&& prev?.vertical === cur?.vertical
		),
	);
}

export function obsGeoAndFilterUpdates($:Observable<FoodLandingItemsParams>) {
	return $.pipe(
		distinctUntilChanged((prev,cur) => true
			&& prev?.latitude === cur?.latitude
			&& prev?.longitude === cur?.longitude
			&& prev?.primary_food_filter === cur?.primary_food_filter
			&& prev?.secondary_filters === cur?.secondary_filters
			&& prev?.vertical === cur?.vertical
		),
	);
}

// ############################################################
// PARSER FUNCTIONS
// ############################################################

export type DealItem = Deal & { origin:FilterType };
export type RestaurantDeal = Omit<DealItem, 'restaurant_ids'> & { restaurantId: number };

export function parseDeals(payload?:Deals) {
	// list all deals/promos
	const restaurantIDDeals = ['marketing_labels', 'menu_deal_buy_1_get_1_free', 'menu_deal_discount', 'promo_delivery', 'promo_food_total'];
	const list = Object.entries(payload ?? {})
	.filter(([k,v]) => restaurantIDDeals.includes(k))
	.map(([k,v]) => [k, v] as [FilterType, Deal[]])
	.filter(([k,v]) => !!v?.length)
	.reduce((a,[k,v]) => a.concat(...v.map(d => ({ ...d, origin: k}))), [] as DealItem[]);

	// give each selector a list of its deals and a list of all the restaurant ids in those deals
	const selectors = payload?.selectors?.map(s => {
		const deals = list.filter(d => s.filtering_type === 'ALL' || d.filtering_type === s.filtering_type);
		const ids = deals.map(d => d.restaurant_ids).flat();
		return {
			...s,
			deals,
			ids: [...new Set(ids)], // force unique
		}
	});

	// map a list of deal to each restaurant id
	const restaurants = new Map<number, RestaurantDeal[]>();
	list.forEach(deal => {
		deal.restaurant_ids.forEach(id => {
			const list = restaurants.get(id) ?? [];
			list.push({ ...deal, restaurantId: id });
			restaurants.set(id, list);
		})
	})
	// sort each restaurant's deal list by `sort`
	restaurants.forEach(list => list.sort((a,b) => (a.sort ?? 0) - (b.sort ?? 0)));

	return { ...payload, list, selectors, restaurants };
}

// ############################################################
// RESTAURANT INDEXING
// ############################################################

export async function registerRestaurantsSemiStatic(body: any, key: IDBValidKey) {
	return regRestaurants('semi_static', body, key);
}
export async function registerRestaurantsDynamic(body: any, key: IDBValidKey) {
	return regRestaurants('dynamic', body, key);
}
async function regRestaurants(type: 'semi_static' | 'dynamic', body: any, key: IDBValidKey) {
	const list = (body.semi_static ?? body.dynamic ?? []) as {id:number}[];
	if (list.length === 0) return;
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB restaurant caching.');
	}
	if (!db) return;
	const t = transaction(db, 'restaurants', 'readwrite');
	const os = t.os('restaurants');
	list.forEach((item,index) => os.put({ id: item.id, type, key, index}));
	// transact(t); // todo: ?
}

export async function lookupRestaurant(id: number, type: 'semi_static'):Promise<RestaurantSemiStatic>;
export async function lookupRestaurant(id: number, type: 'dynamic'):Promise<RestaurantDynamic>;

export async function lookupRestaurant<T = RestaurantSemiStatic | RestaurantDynamic>(id: number, type: 'semi_static'|'dynamic') {
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for reading restaurant cache.');
	}
	if (!db) return null;
	const osRestaurants = transaction(db, 'restaurants', 'readonly').os('restaurants');
	const reg = await osRestaurants.get<{key:IDBValidKey, index:number}>([id,type]);
	if (!reg) return null;
	const osCache = transaction(db, 'cache', 'readonly').os('cache');
	const {payload} = await osCache.get<{payload:{dynamic?:T[],semi_static?:T[]}}>(reg.key);
	const list = payload?.semi_static ?? payload?.dynamic;
	return list?.[reg.index] as T;
}

export function gatherRestaurantCache(restaurantId: number) {
	if (!browser) return Promise.resolve(null);
	return Promise.all([
		retrieveRestaurantExposed(restaurantId),
		lookupRestaurant(restaurantId, 'semi_static'),
		lookupRestaurant(restaurantId, 'dynamic'),
	]).then(([exposed, ss, dyn]) => getRestaurantState({ ...exposed, ...ss, ...dyn } as Parameters<typeof getRestaurantState>[0]));
}

/**
 * Sorts list of restaurant IDs using a set of business rules
 * @param restaurantIDs A list of restaurant IDs
 * @param sortType Informs us which business rules to use
 * @param dynamicData The dynamic data that platform sends us, implicitly containing the dynamic
 * ordering of all the restaurants
 * @returns Sorted list of restaurant IDs
 */
export function sort(
	restaurantIds: number[],
	sortType: SortType = "none",
	dynamicData?: { dynamic: (Pick<ValueFromArray<RestaurantsDynamic["dynamic"]>, "id">)[] },
) {
	if (
		!restaurantIds.length
		|| !dynamicData
		|| sortType === "none"  // This `sortType` requires restaurant IDs to be left as received
	) {
		return restaurantIds;
	}

	// NOTE: sortType === "dynamic"  // this is the only other `sortType` at this point
	// This `sortType` requires restaurants to be sorted according to the order that they appear in
	// on the `dynamic` list.
	const { dynamic } = dynamicData;
	
	const order = restaurantIds.reduce((dict, id) => {
		dict[id] = dynamic.findIndex((d) => d.id === id);
		return dict;
	}, {} as Record<number, number>);

	return [...restaurantIds].sort((idA, idB) => {
		// If no dynamic data found for a restaurant, de-prioritise it
		if (order[idA] < 0 && order[idB] < 0) return 0;
		if (order[idA] < 0) return 1;
		if (order[idB] < 0) return -1;

		// if A's position is smaller than B's, put it before B
		return order[idA] - order[idB];
	});
}
